<template>
  <AppLayout title="Home">
    <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8">
      <H3Text>Quick Links</H3Text>
      <ActionsWithSharedBorders class="mt-2" :actions="actions" />
    </div>
  </AppLayout>
</template>

<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import ActionsWithSharedBorders from '@/Common/Lists/GridLists/ActionsWithSharedBorders.vue'
import H3Text from '@/Common/Text/H3Text.vue'

import {
  CalendarIcon,
  ClockIcon,
  LocationMarkerIcon,
  PaperAirplaneIcon,
} from '@heroicons/vue/outline'
import { computed } from 'vue'
import { getMunicipalitiesFromTripLegs } from './Trips/Common'
import { useUser } from '@/Util/UseUser'

const props = defineProps({
  upcomingTrip: Object,
  plane: Object,
})

const upcomingTripSlot = computed(() => {
  const title = props.upcomingTrip ? 'View upcoming trip' : 'View my trips'

  const href = props.upcomingTrip
    ? route('trips.show', props.upcomingTrip)
    : route('trips.index')

  const description = props.upcomingTrip
    ? `Trip to ${getMunicipalitiesFromTripLegs(props.upcomingTrip.legs)} on ${
        props.upcomingTrip.formatted_departure_date
      }`
    : 'See all past and upcoming trips.'

  return {
    title: title,
    href: href,
    icon: LocationMarkerIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    description: description,
    can: true,
  }
})

const planeSlot = computed(() => {
  const title = props.plane
    ? `View ${props.plane.tail_number}`
    : 'View my planes'

  const href = props.plane
    ? route('planes.show', props.plane)
    : route('planes.index')

  return {
    title: title,
    href: href,
    icon: PaperAirplaneIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    description: 'View all trips for my planes and create new trips.',
    can: true,
  }
})

const actions = computed(() => {
  return [
    {
      title: 'Schedule a trip',
      href: route('trips.create'),
      icon: ClockIcon,
      iconForeground: 'text-teal-700',
      iconBackground: 'bg-teal-50',
      description:
        'Add a new trip to the schedule by choosing airports and dates.',
      can: useUser.value.authorizations.trips.create,
    },
    planeSlot.value,
    upcomingTripSlot.value,
    {
      title: 'Trip Calendar',
      href: route('calendar.index'),
      icon: CalendarIcon,
      iconForeground: 'text-yellow-700',
      iconBackground: 'bg-yellow-50',
      description: 'All trip legs shown by day in a monthly calendar.',
      can: true,
    },
  ].filter((el) => el.can)
})
</script>
